import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BsClipboardCheck } from 'react-icons/bs';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import EmployeeCard from '../components/taskSheet/EmployeeCard';
import TaskSheetToolbar from '../components/taskSheet/TaskSheetToolbar';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchTaskSheetSummary } from '../redux/actions/taskSheetActions';
import { EmployeeTaskSummary } from '../redux/types/taskSheetTypes';

const formatDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getDefaultDates = () => {
  const now = new Date();
  return {
    from: formatDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: formatDate(now),
  };
};

const TaskSheet = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { summaries, loadingSummaries, error } = useAppSelector(s => s.taskSheet);
  const [searchParams, setSearchParams] = useSearchParams();

  const defaults = getDefaultDates();

  // Date state lives in URL
  const dateFrom = searchParams.get('dateFrom') ?? defaults.from;
  const dateTo   = searchParams.get('dateTo')   ?? defaults.to;

  // Write defaults to URL on first load so dates are always visible in the URL bar
  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (!next.get('dateFrom')) next.set('dateFrom', defaults.from);
      if (!next.get('dateTo'))   next.set('dateTo',   defaults.to);
      return next;
    }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDateRange = useCallback((from: string, to: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('dateFrom', from);
      next.set('dateTo', to);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Search stays local (debounced, transient — no need in URL)
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const handleSearchChange = useCallback((v: string) => {
    setSearch(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(v), 350);
  }, []);
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  useEffect(() => {
    dispatch(fetchTaskSheetSummary({ dateFrom, dateTo }));
  }, [dispatch, dateFrom, dateTo]);

  const filtered: EmployeeTaskSummary[] = summaries.filter(e => {
    if (!debouncedSearch) return true;
    const q = debouncedSearch.toLowerCase();
    return (
      e.fullname.toLowerCase().includes(q) ||
      e.designation.toLowerCase().includes(q) ||
      e.employeeCode.includes(q)
    );
  });

  return (
    <ComponentWrapper icon={<BsClipboardCheck />} title="Task Sheet">
      <div className="flex flex-col gap-6">
        <TaskSheetToolbar
          search={search}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onSearchChange={handleSearchChange}
          onDateChange={setDateRange}
        />

        {loadingSummaries ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 animate-pulse">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-11 h-11 rounded-full bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-40" />
                    <div className="h-3 bg-gray-100 rounded w-28" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-100 rounded" />
                  <div className="h-3 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-48 text-red-400 text-base">
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-gray-400 text-lg italic">
            No employees found.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map(emp => (
              <EmployeeCard
                key={emp.employeeId}
                employee={emp}
                onViewMore={stringId => navigate(`/task-sheet/employee/${stringId}?dateFrom=${dateFrom}&dateTo=${dateTo}`)}
              />
            ))}
          </div>
        )}
      </div>
    </ComponentWrapper>
  );
};

export default TaskSheet;
